"use client";

import { useState } from "react";
import { useEditor } from "@/app/editor/_components/SharedImageScene";

const DEMO_ORIGINAL =
  "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=720&h=900&fit=crop";
const DEMO_RESULT =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=720&h=900&fit=crop";

/** Dev-only: seed gallery result UI without upload/processing. */
export default function GallerySceneDevPanel() {
  const {
    isGalleryEnabled,
    setOriginalImage,
    setIsProcessing,
    setProcessedImage,
    setProcessedImageInternal,
    clearGallery,
    galleryImages,
    currentGalleryIndex,
  } = useEditor();
  const [collapsed, setCollapsed] = useState(false);
  const [busy, setBusy] = useState(false);

  if (process.env.NODE_ENV !== "development" || !isGalleryEnabled) return null;

  const seedGallery = async () => {
    if (!setIsProcessing || !setProcessedImage) return;
    setBusy(true);
    try {
      await clearGallery?.();
      setProcessedImageInternal?.(null);
      // Bust cache / force originalImage effect when re-seeding
      const stamp = Date.now();
      setOriginalImage(`${DEMO_ORIGINAL}&dev=${stamp}`);
      // Let original land in gallery, then run processing → result flow
      await new Promise((r) => setTimeout(r, 50));
      setIsProcessing(true);
      await setProcessedImage(`${DEMO_RESULT}&dev=${stamp}`, `dev-mock-${stamp}`);
    } finally {
      setBusy(false);
    }
  };

  const resetUpload = async () => {
    setBusy(true);
    try {
      await clearGallery?.();
      setProcessedImageInternal?.(null);
      setOriginalImage(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed bottom-20 md:bottom-4 left-4 z-[99999] flex flex-col overflow-hidden rounded-xl border border-amber-300 bg-white shadow-2xl"
      style={{ maxWidth: "220px" }}
    >
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="flex items-center justify-between gap-2 bg-amber-500 px-3 py-2 text-left text-xs font-semibold text-white"
      >
        <span>Gallery UI debug</span>
        <span className="text-white/80">{collapsed ? "▸" : "▾"}</span>
      </button>

      {!collapsed && (
        <div className="flex flex-col gap-1.5 p-2">
          <button
            type="button"
            disabled={busy}
            onClick={seedGallery}
            className="rounded-lg bg-gray-900 px-3 py-2 text-left text-xs font-medium text-white hover:bg-gray-800 disabled:opacity-50"
          >
            {busy ? "Seeding…" : "Seed gallery + result"}
          </button>
          <button
            type="button"
            disabled={busy}
            onClick={resetUpload}
            className="rounded-lg bg-gray-100 px-3 py-2 text-left text-xs font-medium text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          >
            Reset to upload
          </button>
          <p className="px-1 pt-0.5 text-[10px] leading-snug text-gray-400">
            Dev only · all gallery tools · slides: {galleryImages?.length ?? 0} ·
            idx: {currentGalleryIndex ?? 0}
          </p>
        </div>
      )}
    </div>
  );
}
